"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CombinedError, useClient } from "urql";
import { logger } from "@/lib/logger";
import {
  fetchWorkspaceSidebarData,
  isDashboardOnlyError,
  isOnboardingOnlyError,
  isProjectOnlyError,
} from "../_components/layout/sidebar/workspace-sidebar-data-fetch";
import type { WorkspaceSidebarData } from "../_components/layout/sidebar/workspace-sidebar-data-model";

type WorkspaceSidebarRefreshReason =
  | "project-mutation"
  | "view-mutation"
  | "dashboard-mutation"
  | "dataset-mutation"
  | "onboarding-mutation"
  | "order-mutation"
  | "ai-tool-mutation"
  | "manual-retry";

export type WorkspaceSidebarDataValue = {
  data: WorkspaceSidebarData | null;
  error: CombinedError | undefined;
  isColdLoading: boolean;
  isRefreshing: boolean;
  refresh(reason: WorkspaceSidebarRefreshReason): void;
};

const WorkspaceSidebarDataContext =
  createContext<WorkspaceSidebarDataValue | null>(null);

/**
 * 認可 scope ごとのサイドバー snapshot を管理する。
 * 親 layout が workspace と user scope で provider を keying するため、
 * remount 後に以前の認可 scope で取得したデータを公開しない。
 */
export function WorkspaceSidebarDataProvider({
  workspaceId,
  children,
}: {
  workspaceId: string;
  children: ReactNode;
}): ReactNode {
  const client = useClient();
  const [lastSuccessfulData, setLastSuccessfulData] =
    useState<WorkspaceSidebarData | null>(null);
  const [error, setError] = useState<CombinedError | undefined>();
  const [isFetching, setIsFetching] = useState(false);
  const lastSuccessfulDataRef = useRef<WorkspaceSidebarData | null>(null);
  const activeRequestCountRef = useRef(0);
  const nextRequestIdRef = useRef(0);
  const latestAppliedRequestIdRef = useRef(0);
  const currentWorkspaceIdRef = useRef(workspaceId);
  const scopeActiveRef = useRef(true);
  const coldRequestScopeRef = useRef<string | null>(null);
  const refreshInFlightRef = useRef(false);
  const refreshPendingRef = useRef(false);
  currentWorkspaceIdRef.current = workspaceId;

  // 初回取得と更新取得に共通する request lifecycle をここへ集約する。
  // request ID と workspace ID の両方を照合して、workspace 切替前の遅延 response や
  // 古い並行 request が、現在の認可 scope の snapshot を上書きしないようにする。
  // refresh は同時に複数の mutation が成功しても1 requestへまとめ、取得失敗時は
  // last-successful snapshot を残すことで、表示中の sidebar を空にしない。
  const executeRequest = useCallback(
    (kind: "cold" | "refresh") => {
      if (kind === "refresh" && refreshInFlightRef.current) {
        // 実行中のqueryより後でmutationが成功した可能性があるため、通知を捨てない。
        // 連打分を1件にまとめつつ、完了後に最新状態を取得するtrailing refreshを予約する。
        refreshPendingRef.current = true;
        return;
      }
      if (kind === "refresh") refreshInFlightRef.current = true;

      const requestWorkspaceId = workspaceId;
      const requestId = ++nextRequestIdRef.current;
      const shouldIgnoreResponse = () =>
        !scopeActiveRef.current ||
        currentWorkspaceIdRef.current !== requestWorkspaceId ||
        requestId < latestAppliedRequestIdRef.current;
      activeRequestCountRef.current += 1;
      if (lastSuccessfulDataRef.current === null) setError(undefined);
      setIsFetching(true);

      void fetchWorkspaceSidebarData(
        client,
        requestWorkspaceId,
        () => lastSuccessfulDataRef.current,
      )
        .then((result) => {
          if (shouldIgnoreResponse()) return;

          const hadSuccessfulData = lastSuccessfulDataRef.current !== null;
          // error付きpartial responseで正常snapshotを壊さない。Onboardingだけの
          // errorは上のfallbackでcore dataを再構成しているため、安全に適用できる。
          if (
            result.data &&
            (!result.error ||
              isOnboardingOnlyError(result.error) ||
              isDashboardOnlyError(result.error) ||
              (isProjectOnlyError(result.error) && !hadSuccessfulData))
          ) {
            latestAppliedRequestIdRef.current = requestId;
            lastSuccessfulDataRef.current = result.data;
            setLastSuccessfulData(result.data);
          }
          setError(result.error);

          if (result.error) {
            const metadata = {
              workspaceId: requestWorkspaceId,
              operation: "WorkspaceSidebarData",
              phase: hadSuccessfulData ? "refresh" : "cold-load",
            };
            if (
              isOnboardingOnlyError(result.error) ||
              isDashboardOnlyError(result.error) ||
              isProjectOnlyError(result.error)
            ) {
              logger.warn(
                "WorkspaceSidebarDataProvider: sidebar data partially unavailable",
                result.error,
                metadata,
              );
            } else {
              logger.error(result.error, metadata);
            }
          }
        })
        .catch((cause: unknown) => {
          if (shouldIgnoreResponse()) return;

          const unexpectedError = new CombinedError({
            networkError:
              cause instanceof Error ? cause : new Error(String(cause)),
          });
          setError(unexpectedError);
          logger.error(unexpectedError, {
            workspaceId: requestWorkspaceId,
            operation: "WorkspaceSidebarData",
            phase: lastSuccessfulDataRef.current ? "refresh" : "cold-load",
          });
        })
        .finally(() => {
          activeRequestCountRef.current -= 1;
          if (kind === "refresh") {
            refreshInFlightRef.current = false;
            if (refreshPendingRef.current && scopeActiveRef.current) {
              refreshPendingRef.current = false;
              executeRequest("refresh");
            }
          }
          if (scopeActiveRef.current && activeRequestCountRef.current === 0) {
            setIsFetching(false);
          }
        });
    },
    [client, workspaceId],
  );

  useEffect(() => {
    // なぜ useEffect が必要か:
    // App Router の workspace 遷移では、次の RSC が commit される前に開始した query の
    // subscription が認可 scope の切替時に破棄され、成功 response を描画へ反映できない。
    // provider の認可 scope が画面へ commit された後に取得を始め、unmount 時にその scope
    // を無効化する必要があるため、この lifecycle は effect で管理する。
    //
    // 何をしているか:
    // workspace scopeが画面へcommitされた直後、network-onlyの初回取得を1回開始する。
    // 取得中はsidebar skeletonを表示し、layout自体はsidebar queryを待たずに描画する。
    // scope ref はStrict Modeのeffect再実行を抑止し、cleanupとworkspace IDの照合で
    // 切替前requestの遅延responseが現在のsnapshotへ混入することを防ぐ。
    scopeActiveRef.current = true;
    if (
      lastSuccessfulDataRef.current === null &&
      coldRequestScopeRef.current !== workspaceId
    ) {
      coldRequestScopeRef.current = workspaceId;
      executeRequest("cold");
    }

    return () => {
      scopeActiveRef.current = false;
    };
  }, [executeRequest, workspaceId]);

  const refresh = useCallback(
    (_reason: WorkspaceSidebarRefreshReason) => {
      executeRequest("refresh");
    },
    [executeRequest],
  );

  const value = useMemo<WorkspaceSidebarDataValue>(
    () => ({
      data: lastSuccessfulData,
      error,
      isColdLoading: lastSuccessfulData === null && !error,
      isRefreshing: lastSuccessfulData !== null && isFetching,
      refresh,
    }),
    [error, isFetching, lastSuccessfulData, refresh],
  );

  return (
    <WorkspaceSidebarDataContext.Provider value={value}>
      {children}
    </WorkspaceSidebarDataContext.Provider>
  );
}

/** 現在の provider snapshot と loading/error state を返す。 */
export function useWorkspaceSidebarData(): WorkspaceSidebarDataValue {
  const value = useContext(WorkspaceSidebarDataContext);
  if (!value) {
    throw new Error(
      "useWorkspaceSidebarData must be used within WorkspaceSidebarDataProvider",
    );
  }
  return value;
}

/** サイドバー関連 mutation が共有する non-blocking refresh 関数を返す。 */
export function useRefreshWorkspaceSidebar(): WorkspaceSidebarDataValue["refresh"] {
  return useWorkspaceSidebarData().refresh;
}
